import { fieldEntryHasPresentableValue } from '@/common/utils/changedDataFieldMerge'
import { mergeFieldMaps } from '@/common/utils/jsonUniData'
import { looksLikePlainUrl } from '@/common/utils/mhtmlPlainUrl'
import { normalizeFormElementType } from '@/common/utils/formElementType'
import { coerceStateBoolean } from '@/common/utils/resolveActionPageState'
import { collectFormLayoutElements } from '@/common/utils/validateFormElementNames'
import type { VisibilityStatePatch } from '@/common/utils/visibilityStatePatches'
import { setType } from '@/common/utils/typedValueSerializer'
import { unwrapScriptValue } from '@/common/utils/wrapScriptValue'

const TEXT_LIKE_LAYOUT_TYPES = new Set(['TEXT', 'TEXTAREA', 'MULTILINETEXT'])

function looksLikeHtmlContent(value: string): boolean {
  const trimmed = value.trim()
  if (!trimmed) return false
  return /<\/?[a-z][\s\S]*>/i.test(trimmed) || trimmed.includes('&lt;') || trimmed.includes('&gt;')
}

function readFieldScalar(fields: Record<string, unknown>, fieldName: string): string | null {
  const entry = fields[fieldName]
  if (entry == null) return null
  if (typeof entry === 'object' && !Array.isArray(entry) && 'value' in entry) {
    const raw = (entry as { value?: unknown }).value
    if (raw == null) return null
    const text = String(unwrapScriptValue(raw)).trim()
    return text === '' ? null : text
  }
  if (typeof entry === 'string') {
    const text = entry.trim()
    return text === '' ? null : text
  }
  if (fieldEntryHasPresentableValue(entry)) {
    const text = String(unwrapScriptValue(entry)).trim()
    return text === '' ? null : text
  }
  return null
}

function isFieldEmpty(fields: Record<string, unknown>, fieldName: string): boolean {
  return readFieldScalar(fields, fieldName) == null
}

function isCopyableScalarForHtmlField(value: string): boolean {
  return looksLikePlainUrl(value) || looksLikeHtmlContent(value)
}

function normalizeLayoutType(type: string): string {
  return normalizeFormElementType({ type: { _text: type } }) ?? type.trim().toUpperCase()
}

export function syncHiddenTextToVisibleHtmlFields(
  fields: Record<string, unknown>,
  visibilityPatches: VisibilityStatePatch[],
  formRoot: unknown,
): Record<string, unknown> {
  if (!fields || typeof fields !== 'object') return fields

  const hiddenNames = new Set<string>()
  for (const patch of visibilityPatches) {
    if (coerceStateBoolean(patch.is_visible) !== false) continue
    const name = String(patch.element_name ?? '').trim()
    if (name) hiddenNames.add(name)
  }
  if (hiddenNames.size === 0) return fields

  const layoutElements = collectFormLayoutElements(formRoot)
  if (layoutElements.length === 0) return fields

  const out: Record<string, unknown> = { ...fields }
  const byPanel = new Map<string, typeof layoutElements>()

  for (const element of layoutElements) {
    const panelId = String(element.panelId ?? '').trim()
    if (!panelId) continue
    const bucket = byPanel.get(panelId) ?? []
    bucket.push(element)
    byPanel.set(panelId, bucket)
  }

  for (const panelElements of byPanel.values()) {
    const htmlElements = panelElements.filter(
      (element) => normalizeLayoutType(element.type) === 'HTML',
    )
    const textElements = panelElements.filter((element) =>
      TEXT_LIKE_LAYOUT_TYPES.has(normalizeLayoutType(element.type)),
    )
    if (htmlElements.length === 0 || textElements.length === 0) continue

    for (const htmlElement of htmlElements) {
      if (hiddenNames.has(htmlElement.name)) continue
      if (!isFieldEmpty(out, htmlElement.name)) continue

      for (const textElement of textElements) {
        if (!hiddenNames.has(textElement.name)) continue
        const scalar = readFieldScalar(out, textElement.name)
        if (!scalar || !isCopyableScalarForHtmlField(scalar)) continue
        out[htmlElement.name] = setType(scalar)
        break
      }
    }
  }

  return out
}

export function mergeOpenFormFieldMapsForOnLoad(response: Record<string, unknown>): Record<string, unknown> {
  const uniFields = ((response.uniData as { fields?: { fields?: Record<string, unknown> } } | undefined)?.fields
    ?.fields ?? {}) as Record<string, unknown>
  const changedRoot = (response.jsResult as { changed_data?: { fields?: { fields?: Record<string, unknown> } } } | undefined)
    ?.changed_data?.fields?.fields
  return mergeFieldMaps(uniFields, changedRoot ?? {})
}
